/**
 * Générateur — Connaître l'unité centimètre cube
 * Attendu ID : 647 | Niveau : 6ème | Domaine : Grandeurs et mesures
 *
 * Types générés : qcm_unique, relier, texte_trou_saisie
 * Labels de référence :
 *   - Identifier la définition du cm³ (cube d'arête 1 cm)
 *   - Distinguer unités d'aire et unités de volume
 * Variantes estimées : 300+
 */

import {
    randomChoice, shuffle,
    makeQcm, makeRelier, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 647;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DEF = 'Identifier la définition du cm³ (cube d\'arête 1 cm)';

// ─── Pools ───

/** Définitions correctes du cm³ (variantes de formulation) */
const DEFINITIONS_CORRECTES = [
    'Le volume d\'un cube d\'arête 1 cm',
    'Le volume d\'un cube dont chaque arête mesure 1 cm',
    'Le volume occupé par un cube de 1 cm de côté',
];

/** Distracteurs de définition */
const DISTRACTEURS_DEF = [
    'Le volume d\'un cube d\'arête 1 m',
    'L\'aire d\'un cube d\'arête 1 cm',
    'Le volume d\'un carré de côté 1 cm',
    'L\'aire d\'un carré de côté 1 cm',
    'Le périmètre d\'un cube d\'arête 1 cm',
    'Le volume d\'un cube d\'arête 1 mm',
    'Le volume d\'un cube d\'arête 1 dm',
    'La surface d\'un cube d\'arête 1 cm',
];

/** Questions QCM variées */
const QUESTIONS_QCM = [
    'Quelle est la définition de 1 cm³ ?',
    'Que représente un centimètre cube ?',
    'Le centimètre cube (cm³) correspond à :',
    '1 cm³, c\'est :',
];

/** Grandeurs et leurs unités pour l'exercice relier */
const GRANDEURS_UNITES = [
    { source: 'Longueur', target: 'cm' },
    { source: 'Aire', target: 'cm²' },
    { source: 'Volume', target: 'cm³' },
    { source: 'Périmètre', target: 'cm' },
    { source: 'Masse', target: 'g' },
    { source: 'Durée', target: 's' },
];

/** Unités et leur nature pour un autre type de relier */
const UNITE_NATURE = [
    { source: 'cm', target: 'Longueur' },
    { source: 'cm²', target: 'Aire' },
    { source: 'cm³', target: 'Volume' },
    { source: 'm²', target: 'Aire' },
    { source: 'm³', target: 'Volume' },
    { source: 'dm', target: 'Longueur' },
    { source: 'mm²', target: 'Aire' },
    { source: 'dm³', target: 'Volume' },
];

/** Phrases à trous avec variantes — les trous ne contiennent que des mots simples (pas de symboles ³ ²). */
const PHRASES_TROUS = [
    {
        phrase: 'Le centimètre cube est le {{volume}} d\'un {{cube}} d\'arête {{1}} cm.',
        texte: 'Complète la définition du centimètre cube.',
    },
    {
        phrase: '1 cm³ est le volume d\'un {{cube}} dont chaque {{arête}} mesure {{1}} cm.',
        texte: 'Complète la phrase.',
    },
    {
        phrase: 'L\'unité de volume utilisée est le centimètre {{cube}}. Il correspond au volume d\'un cube d\'arête {{1}} cm.',
        texte: 'Complète la définition.',
    },
    {
        phrase: 'Le cm³ est une unité de {{volume}}. Le cm² est une unité d\'{{aire}}.',
        texte: 'Complète en distinguant aire et volume.',
    },
    {
        phrase: 'Le volume d\'un cube d\'{{arête}} 1 cm vaut {{1}} cm³.',
        texte: 'Complète la phrase.',
    },
    {
        phrase: 'Pour mesurer un {{volume}}, on utilise le cm³ ; pour mesurer une {{aire}}, on utilise le cm² ; pour mesurer une {{longueur}}, on utilise le cm.',
        texte: 'Complète en associant chaque grandeur à son unité.',
    },
    {
        phrase: 'Un dé dont l\'arête mesure 1 cm occupe un {{volume}} de {{1}} cm³.',
        texte: 'Complète la phrase.',
    },
    {
        // Trou déplacé sur le nombre (revue 2026-07-31) : avec {{m}}, l'élève
        // tapant « mètre » écrivait une phrase vraie rejetée (comparaison
        // texte). Symétrique de la phrase 1 (« d'arête {{1}} cm »).
        phrase: 'Le mètre cube est le volume d\'un {{cube}} d\'arête {{1}} m.',
        texte: 'Complète la définition du mètre cube.',
    },
];

// ═══════════════════════════════════════════
// EXERCICE — Définition du cm³
// ═══════════════════════════════════════════

const genDefQcm = () => {
    const correct = randomChoice(DEFINITIONS_CORRECTES);
    const question = randomChoice(QUESTIONS_QCM);
    const distracteurs = shuffle([...DISTRACTEURS_DEF]).slice(0, 3);
    const choices = [correct, ...distracteurs];
    const texte = `<p>${question}</p>`;
    const payload = makeQcm(choices, correct, texte, opts(L_DEF));
    payload._meta.generatorKey = 'definition_cm3';
    return payload;
};

/** Filtre un pool pour n'avoir que des targets uniques (et des sources uniques) */
const pickUniquePairs = (obligatoire, pool, n) => {
    const result = [obligatoire];
    const usedTargets = new Set([obligatoire.target]);
    const usedSources = new Set([obligatoire.source]);
    const shuffled = shuffle([...pool]);
    for (const p of shuffled) {
        if (result.length >= n) break;
        if (!usedTargets.has(p.target) && !usedSources.has(p.source)) {
            usedTargets.add(p.target);
            usedSources.add(p.source);
            result.push(p);
        }
    }
    return result;
};

const genDefRelier = () => {
    // Choisir un des deux modes
    const mode = Math.random() < 0.5 ? 'grandeur_unite' : 'unite_nature';

    if (mode === 'grandeur_unite') {
        const obligatoire = GRANDEURS_UNITES.find(g => g.source === 'Volume');
        const pairs = pickUniquePairs(obligatoire, GRANDEURS_UNITES.filter(g => g.source !== 'Volume'), 4);
        const texte = '<p>Relie chaque grandeur à son unité.</p>';
        const payload = makeRelier(pairs, texte, opts(L_DEF));
        payload._meta.generatorKey = 'definition_cm3';
        return payload;
    }

    // Mode : unité → nature (longueur / aire / volume)
    const obligatoire = UNITE_NATURE.find(u => u.source === 'cm³');
    const pairs = pickUniquePairs(obligatoire, UNITE_NATURE.filter(u => u.source !== 'cm³'), 4);
    const texte = '<p>Relie chaque unité à la grandeur qu\'elle mesure.</p>';
    const payload = makeRelier(pairs, texte, opts(L_DEF));
    payload._meta.generatorKey = 'definition_cm3';
    return payload;
};

const genDefTrou = () => {
    const item = randomChoice(PHRASES_TROUS);
    const texte = `<p>${item.texte}</p>`;
    const payload = makeTexteTrou(item.phrase, texte, { ...opts(L_DEF), mode: 'saisie', strict: false });
    payload._meta.generatorKey = 'definition_cm3';
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    definition_cm3_qcm_unique:       genDefQcm,
    definition_cm3_relier:           genDefRelier,
    definition_cm3_texte_trou_saisie: genDefTrou,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'definition_cm3', label: L_DEF, formatsList: buildFormats('definition_cm3') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
